import {
  DEFAULT_CONFIGURATIONS,
  WindowOrder,
} from '@cutr/constants/windows/windows';
import { useRouteLoaderData } from 'react-router-dom';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { hashObject, pick } from '@/utils/misc';

type WindowUpdateableFields =
  | 'title'
  | 'configurations'
  | 'customerMarkupPercentage'
  | 'customerTaxPercentage';

type WindowState = Pick<WindowOrder, WindowUpdateableFields> &
  Pick<WindowOrder, 'status'> & {
    hash: number;
  };

const DEFAULT_STORE: WindowState = {
  status: 'draft',
  title: '',
  configurations: {
    ...DEFAULT_CONFIGURATIONS,
  },
  customerMarkupPercentage: 0,
  customerTaxPercentage: 0,
  hash: 0,
};

export type WindowStoreActions = {
  init: (order: WindowOrder) => void;
  reset: () => void;

  setTitle: (title: string) => void;
  setCustomerMarkupPercentage: (customerMarkupPercentage: number) => void;
  setCustomerTaxPercentage: (customerTaxPercentage: number) => void;
  /**
   * @deprecated this should only be used in a ConfigurationsProvider's onConfigurationChange
   */
  setConfigurations: (configurations: WindowOrder['configurations']) => void;

  setHash: (hash: number) => void;
};

export const useWindowLoaderData = () => {
  const windowsLoaderData = useRouteLoaderData('windows/orders/:orderId');
  const agentLoaderData = useRouteLoaderData('agent/windows/orders/:orderId');
  return (windowsLoaderData || agentLoaderData) as WindowOrder;
};

export const useWindowStore = create<WindowState & WindowStoreActions>()(
  devtools(
    (set) => ({
      ...DEFAULT_STORE,
      setTitle: (title) => set(() => ({ title })),
      setConfigurations: (configurations) => set(() => ({ configurations })),
      setCustomerMarkupPercentage: (customerMarkupPercentage: number) =>
        set(() => ({ customerMarkupPercentage })),
      setCustomerTaxPercentage: (customerTaxPercentage: number) =>
        set(() => ({ customerTaxPercentage })),
      init: (order) =>
        set(() => {
          return { ...order, hash: hashOrder(order) };
        }),
      reset: () => set(() => ({ ...DEFAULT_STORE })),
      setHash: (hash) => set(() => ({ hash })),
    }),
    { name: 'window-store' }
  )
);

export const hashOrder = (order: WindowOrder | WindowState) => {
  const windowOrderKeys = Object.keys(DEFAULT_STORE).filter(
    (key) => key !== 'hash'
  );

  const toHash = pick(order, windowOrderKeys);

  return hashObject(toHash);
};
